<template>
  <div v-if="!error && !hasSummary" class="microlearning-view-edit microlearning-view-edit--video-only">
    <div class="microlearning-view-edit__video-container">
      <div
        v-if="isIframeEmbed"
        :key="iframeKey"
        v-html="iframeHtml"
        class="microlearning-view-edit__iframe-container"
      ></div>
      <video
        v-else
        ref="videoPlayer"
        controls
        class="microlearning-view-edit__video"
      >
        <source :src="video" type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    </div>
  </div>

  <div v-else-if="error" class="microlearning-view-edit microlearning-view-edit--with-error">
    <div class="microlearning-view-edit__video-container">
      <div
        v-if="isIframeEmbed"
        :key="iframeKey"
        v-html="iframeHtml"
        class="microlearning-view-edit__iframe-container"
      ></div>
      <video
        v-else
        ref="videoPlayer"
        controls
        class="microlearning-view-edit__video"
      >
        <source :src="video" type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    </div>
    <VideoInstructionsSummary
      :reasonForInvalid="error"
      class="microlearning-view-edit__error"
    />
  </div>

  <div
    v-else
    class="microlearning-view-edit microlearning-view-edit--with-instructions"
    :class="{ 
      'microlearning-view-edit--editable': editable,
      'microlearning-view-edit--compact': compactStepsLayout
    }"
  >
    <div 
      class="microlearning-view-edit__top-row"
      :class="{ 'microlearning-view-edit__top-row--compact': compactStepsLayout }"
    >
      <div ref="videoWrapper" class="microlearning-view-edit__video-wrapper">
        <div
          v-if="isIframeEmbed"
          :key="iframeKey"
          v-html="iframeHtml"
          class="microlearning-view-edit__iframe-container"
        ></div>
        <video
          v-else
          ref="videoPlayer"
          controls
          class="microlearning-view-edit__video"
        >
          <source :src="video" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </div>
      <VideoInstructionsSummary
        :rubric="summary"
        class="microlearning-view-edit__summary"
        :class="{ 
          'microlearning-view-edit__summary--interactive': editable,
          'microlearning-view-edit__summary--static': !editable
        }"
        @click="handleSummaryClick"
      />
    </div>

    <div class="microlearning-view-edit__steps-container" :style="stepsContainerStyle">
      <div class="microlearning-view-edit__steps-list">
        <InstructionStepCard
          v-for="(step, index) in steps"
          :key="step.id || index"
          :competence_category="step.competence_category"
          :inspector_action="step.inspector_action"
          :look_for="step.look_for"
          :pass_criteria="step.pass_criteria"
          :fail_criteria="step.fail_criteria"
          :timestamp_ref="step.timestamp_ref"
          :video_jump_url="step.video_jump_url"
          class="microlearning-view-edit__step-card"
          :class="{ 
            'microlearning-view-edit__step-card--interactive': editable,
            'microlearning-view-edit__step-card--static': !editable
          }"
          @card-click="handleStepClick(step, index)"
          @timestamp-click="handleTimestampClick"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import VideoInstructionsSummary from '@components/VideoInstructionsSummary/VideoInstructionsSummary.vue'
import InstructionStepCard from '@components/InstructionStepCard/InstructionStepCard.vue'

const props = defineProps({
  video: {
    type: String,
    required: true
  },
  error: {
    type: String,
    default: null
  },
  summary: {
    type: Object,
    default: null
  },
  steps: {
    type: Array,
    default: () => []
  },
  editable: {
    type: Boolean,
    default: false
  },
  maxStepsHeight: {
    type: String,
    default: '400px'
  },
  compactStepsLayout: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['summary-click', 'step-click', 'save', 'discard'])

const videoPlayer = ref(null)
const videoWrapper = ref(null)

// Track current video URL for timestamp jumps
const currentVideoUrl = ref(null)

// Key to force iframe re-render when URL changes
const iframeKey = ref(0)

const hasSummary = computed(() => {
  return props.summary && Object.keys(props.summary).length > 0
})

// Computed style for steps container with dynamic max-height
const stepsContainerStyle = computed(() => ({
  maxHeight: props.maxStepsHeight
}))

// Check if video is an api.video embed URL or iframe HTML
const isIframeEmbed = computed(() => {
  if (!props.video) return false
  // Check for iframe HTML
  if (props.video.includes('<iframe') || props.video.includes('iframe')) {
    return true
  }
  // Check for api.video embed URLs
  if (props.video.includes('embed.api.video') || props.video.includes('api.video/vod')) {
    return true
  }
  return false
})

// Track if we should autoplay (when timestamp clicked)
const shouldAutoplay = ref(false)

// Generate iframe HTML for api.video URLs
const iframeHtml = computed(() => {
  if (!props.video) return ''
  
  // If it's already iframe HTML, return as-is
  if (props.video.includes('<iframe')) {
    return props.video
  }
  
  // Use current video URL if set (for timestamp jumps), otherwise use prop
  let videoUrl = currentVideoUrl.value || props.video
  
  // Generate iframe for api.video URLs
  if (videoUrl.includes('embed.api.video') || videoUrl.includes('api.video/vod')) {
    // Add autoplay and muted parameters if timestamp was clicked
    // Note: muted is required for autoplay to work in most browsers
    if (shouldAutoplay.value && !videoUrl.includes('autoplay=')) {
      const separator = videoUrl.includes('?') ? '&' : '?'
      // Insert autoplay before the hash fragment if present
      if (videoUrl.includes('#')) {
        const [baseUrl, hash] = videoUrl.split('#')
        videoUrl = `${baseUrl}${separator}autoplay=true&muted=false#${hash}`
      } else {
        videoUrl = `${videoUrl}${separator}autoplay=true&muted=false`
      }
    }
    return `<iframe src="${videoUrl}" style="width:100%;height:100%;border:none;border-radius:8px;" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen" allowfullscreen></iframe>`
  }
  
  return props.video
})

const handleSummaryClick = () => {
  if (props.editable) {
    emit('summary-click', props.summary)
  }
}

const handleStepClick = (step, index) => {
  if (props.editable) {
    emit('step-click', { step, index })
  }
}

// Handle timestamp click to jump video to specific time
const handleTimestampClick = (videoJumpUrl) => {
  if (videoJumpUrl) {
    // Enable autoplay for this navigation
    shouldAutoplay.value = true
    
    // Update the video URL to jump to timestamp
    currentVideoUrl.value = videoJumpUrl
    
    // Increment key to force iframe re-render (this makes the new URL with autoplay take effect)
    iframeKey.value++
    
    // Scroll to the video section smoothly
    if (videoWrapper.value) {
      videoWrapper.value.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }
}

defineExpose({
  videoPlayer,
  handleTimestampClick
})
</script>

<style scoped>
.microlearning-view-edit {
  display: flex;
  flex-direction: column;
  gap: 22px;
  width: 100%;
}

.microlearning-view-edit__video-container {
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  background: #000;
}

.microlearning-view-edit__video-wrapper {
  flex: 1;
  min-width: 0;
  border-radius: 8px;
  overflow: hidden;
  background: #000;
}

.microlearning-view-edit__video {
  width: 100%;
  height: auto;
  display: block;
  border-radius: 8px;
  aspect-ratio: 571 / 333;
  object-fit: cover;
}

.microlearning-view-edit__iframe-container {
  width: 100%;
  aspect-ratio: 571 / 333;
}

.microlearning-view-edit__iframe-container :deep(iframe) {
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 8px;
}

.microlearning-view-edit__error {
  width: 100%;
}

.microlearning-view-edit__top-row {
  display: flex;
  gap: 22px;
  align-items: stretch;
  width: 100%;
}

.microlearning-view-edit__top-row .microlearning-view-edit__video-wrapper {
  flex: 1;
  max-width: calc(50% - 11px);
  display: flex;
  flex-direction: column;
}

.microlearning-view-edit__top-row .microlearning-view-edit__iframe-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.microlearning-view-edit__summary {
  flex: 1;
  min-width: 0;
  max-width: calc(50% - 11px);
  display: flex;
}

/* Compact layout: stack video and summary vertically */
.microlearning-view-edit__top-row--compact {
  flex-direction: column;
}

.microlearning-view-edit__top-row--compact .microlearning-view-edit__video-wrapper {
  max-width: 100%;
  width: 100%;
}

.microlearning-view-edit__top-row--compact .microlearning-view-edit__summary {
  max-width: 100%;
  width: 100%;
}

.microlearning-view-edit__summary :deep(.video-instructions-summary) {
  height: 100%;
}

.microlearning-view-edit__summary--static {
  pointer-events: none;
}

.microlearning-view-edit__summary--static :deep(.video-instructions-summary) {
  cursor: default;
}

.microlearning-view-edit__summary--static :deep(.video-instructions-summary:hover) {
  background-color: #FFFFFF;
  cursor: default;
}

.microlearning-view-edit__summary--static :deep(.video-instructions-summary:hover .video-instructions-summary__icon) {
  background-color: #FFFFFF;
}

.microlearning-view-edit__summary--interactive {
  cursor: url("../assets/cursorArrow.svg"), auto;
}

.microlearning-view-edit__steps-container {
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 8px;
}

/* Custom scrollbar styling */
.microlearning-view-edit__steps-container::-webkit-scrollbar {
  width: 6px;
}

.microlearning-view-edit__steps-container::-webkit-scrollbar-track {
  background: #F2F4F7;
  border-radius: 3px;
}

.microlearning-view-edit__steps-container::-webkit-scrollbar-thumb {
  background: #D0D5DD;
  border-radius: 3px;
}

.microlearning-view-edit__steps-container::-webkit-scrollbar-thumb:hover {
  background: #98A2B3;
}

.microlearning-view-edit__steps-list {
  display: flex;
  flex-direction: column;
  gap: 22px;
  width: 100%;
}

.microlearning-view-edit__step-card {
  width: 100%;
}

.microlearning-view-edit__step-card--static {
  pointer-events: none;
}

.microlearning-view-edit__step-card--static :deep(.instruction-step-card-timestamp-link) {
  pointer-events: auto;
}

.microlearning-view-edit__step-card--static :deep(.instruction-step-card-content-wrapper:hover) {
  background-color: transparent;
  cursor: default;
}

.microlearning-view-edit__step-card--interactive {
  cursor: url("../assets/cursorArrow.svg"), auto;
}

.microlearning-view-edit--video-only .microlearning-view-edit__video-container {
  max-width: 100%;
}

.microlearning-view-edit--with-error {
  gap: 22px;
}
</style>

